import {Button, Checkbox, Pagination, Tag} from "omoplay";
import {useEffect, useMemo, useState} from "react";
import SuspensePopup from "../../SuspensePopup";
import {matchesSpotKeyword, spotShortId, spotTypeLabel} from "../../../libs/spot";
import {
    attachSpotTag, deleteSpotTag, detachSpotTag, getSpotIdsByTag, renameSpotTag,
} from "../../../api/spot/spot.api";

const PAGE_SIZE = 50

/**
 * 진입점 그룹(해시태그) 관리 화면.
 *
 * 목적 = **고객사 담당자가 매장 그룹을 직접 만들고 그 그룹의 진입점을 관리**하는 곳.
 * 진입점 600개가 넘는 고객사가 이벤트마다 전부 고르는 것이 불가능해서 만들었다.
 *
 * 동작 = 그룹을 고르거나 새로 만들고 → 진입점을 체크/해제 → **저장**.
 * 저장 시 **변경분(diff)만** 서버에 보낸다(붙일 것 attach, 뗄 것 detach).
 *
 * 🔴 태그는 **선택 보조 수단**이다. 여기서 그룹을 바꿔도 **이미 만든 이벤트의 진입점 연결은
 *    바뀌지 않는다.** 그렇게 안내 문구를 항상 띄운다 — 없으면 "그룹에서 뺐는데 이벤트에 남아 있다" 는
 *    오해가 반드시 생긴다.
 */
export const SpotTagManagePopup = ({visible, spots = [], tags = [], tagsBySpot = {}, onClose, onChanged}) => {
    const [selectedTag, setSelectedTag] = useState(null)
    const [newTagName, setNewTagName] = useState("")
    const [keyword, setKeyword] = useState("")
    const [page, setPage] = useState(1)
    // 이 그룹에 속한 진입점 id(편집 중인 상태). 저장 전까지 서버에 반영되지 않는다.
    const [memberIds, setMemberIds] = useState(() => new Set())
    const [originalIds, setOriginalIds] = useState(() => new Set())
    const [busy, setBusy] = useState(null)

    useEffect(() => {
        if (!visible) {
            setSelectedTag(null); setNewTagName(""); setKeyword(""); setPage(1)
            setMemberIds(new Set()); setOriginalIds(new Set()); setBusy(null)
        }
    }, [visible])

    /** 그룹을 고르면 그 멤버를 불러온다. 편집 중이던 내용은 버린다(저장 전 경고). */
    const selectTag = async (tagCode) => {
        if (isDirty && !window.confirm("저장하지 않은 변경이 있습니다. 버리고 이동할까요?")) return
        setKeyword(""); setPage(1)
        if (tagCode == null) {
            setSelectedTag(null); setMemberIds(new Set()); setOriginalIds(new Set())
            return
        }
        setBusy("불러오는 중...")
        try {
            const {data} = await getSpotIdsByTag(tagCode)
            const ids = new Set(data ?? [])
            setSelectedTag(tagCode); setMemberIds(ids); setOriginalIds(new Set(ids))
        } finally {
            setBusy(null)
        }
    }

    const createTag = () => {
        const name = newTagName.trim()
        if (name === "") return
        if (tags.some((t) => t.tagCode === name)) {
            alert("같은 이름의 그룹이 이미 있습니다.")
            return
        }
        // 서버에는 진입점을 붙이는 순간 생긴다(빈 그룹은 저장할 것이 없다).
        setSelectedTag(name); setMemberIds(new Set()); setOriginalIds(new Set())
        setNewTagName(""); setKeyword(""); setPage(1)
    }

    const filtered = useMemo(
        () => spots.filter((spot) => matchesSpotKeyword(spot, keyword)),
        [spots, keyword]
    )
    const filteredCount = filtered.length
    const paged = useMemo(
        () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [filtered, page]
    )

    useEffect(() => {
        const lastPage = Math.max(1, Math.ceil(filteredCount / PAGE_SIZE))
        if (page > lastPage) setPage(lastPage)
    }, [filteredCount, page])

    const added = useMemo(() => [...memberIds].filter((id) => !originalIds.has(id)), [memberIds, originalIds])
    const removed = useMemo(() => [...originalIds].filter((id) => !memberIds.has(id)), [memberIds, originalIds])
    const isDirty = added.length > 0 || removed.length > 0

    const pagedAllChecked = paged.length > 0 && paged.every((spot) => memberIds.has(spot.id))

    const toggleMany = (targets, checked) => {
        setMemberIds((prev) => {
            const next = new Set(prev)
            targets.forEach((spot) => { if (checked) next.add(spot.id); else next.delete(spot.id) })
            return next
        })
    }

    const save = async () => {
        if (selectedTag == null || !isDirty) return
        setBusy("저장 중...")
        try {
            // 붙일 것 / 뗄 것만 보낸다. 둘 다 멱등이라 재시도도 안전하다.
            if (added.length > 0) await attachSpotTag(added, selectedTag)
            if (removed.length > 0) await detachSpotTag(removed, selectedTag)
            setOriginalIds(new Set(memberIds))
            await onChanged()
        } finally {
            setBusy(null)
        }
    }

    const rename = async () => {
        if (selectedTag == null) return
        const next = window.prompt("새 그룹 이름", selectedTag)
        if (next == null) return
        const trimmed = next.trim()
        if (trimmed === "" || trimmed === selectedTag) return
        setBusy("이름 변경 중...")
        try {
            await renameSpotTag(selectedTag, trimmed)
            setSelectedTag(trimmed)
            await onChanged()
        } finally {
            setBusy(null)
        }
    }

    const removeTag = async () => {
        if (selectedTag == null) return
        if (!window.confirm(`그룹 '${selectedTag}' 을 삭제합니다.\n진입점 자체와 이미 만든 이벤트의 연결은 그대로 유지됩니다.`)) return
        setBusy("삭제 중...")
        try {
            await deleteSpotTag(selectedTag)
            setSelectedTag(null); setMemberIds(new Set()); setOriginalIds(new Set())
            await onChanged()
        } finally {
            setBusy(null)
        }
    }

    return (
        <SuspensePopup title="진입점 그룹 관리" visible={visible} style={{width: "1100px"}} className="modal-fit"
                       onClose={() => {
                           if (isDirty && !window.confirm("저장하지 않은 변경이 있습니다. 닫을까요?")) return
                           onClose()
                       }}
        >
            <div className="flex-col gap-4" style={{marginTop: "16px"}}>
                {/* 🔴 오해 방지 문구. 없으면 반드시 "그룹에서 뺐는데 이벤트에 남아 있다" 가 된다. */}
                <div className="text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
                    그룹은 <b>진입점을 묶어서 한 번에 고르기 위한 관리용 묶음</b>입니다.
                    그룹을 바꿔도 <b>이미 만든 이벤트의 진입점 연결은 변경되지 않습니다.</b>
                </div>

                {/* 그룹 선택 · 생성 */}
                <div className="flex-col gap-2">
                    <div className="text-sm font-semibold">그룹</div>
                    <div className="flex gap-2 items-center flex-wrap">
                        {
                            tags.map((t) => (
                                <div key={t.tagCode} className="pointer" onClick={() => selectTag(t.tagCode)}>
                                    <Tag color={selectedTag === t.tagCode ? "info" : undefined}>
                                        {`# ${t.tagCode} (${t.spotCount})`}
                                    </Tag>
                                </div>
                            ))
                        }
                        {tags.length === 0 && <div className="text-sm text-gray-400">아직 만든 그룹이 없습니다.</div>}
                    </div>
                    <div className="flex gap-2 items-center">
                        <input
                            className="block p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white"
                            style={{height: "40px", width: "260px"}}
                            placeholder="새 그룹 이름 (예: 전국, 수도권)"
                            maxLength={40}
                            value={newTagName}
                            onChange={(e) => setNewTagName(e.target.value)}
                            onKeyDown={(e) => { if (e.keyCode === 13) createTag() }}
                        />
                        <Button variants="default" value="+ 그룹 만들기"
                                disabled={newTagName.trim() === ""} onClick={() => createTag()} />
                        {
                            selectedTag != null && (
                                <>
                                    <Button variants="default" value="이름 변경" onClick={() => rename()} />
                                    <Button variants="danger" value="그룹 삭제" onClick={() => removeTag()} />
                                </>
                            )
                        }
                    </div>
                </div>

                {
                    selectedTag == null ? (
                        <div className="flex-center-all py-16 text-gray-400">
                            그룹을 고르거나 새로 만들면 진입점을 지정할 수 있습니다.
                        </div>
                    ) : (
                        <>
                            <div className="flex gap-4 items-center justify-between">
                                <div className="max-w-[405px] w-[405px]">
                                    <input
                                        type="search"
                                        className="block w-full p-2 pr-8 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white"
                                        style={{height: "40px"}}
                                        placeholder="진입점명, ID, 타입으로 검색"
                                        value={keyword}
                                        onChange={(e) => { setPage(1); setKeyword(e.target.value) }}
                                    />
                                </div>
                                <div className="text-sm text-gray-500 whitespace-nowrap">
                                    {`'${selectedTag}' 소속 ${memberIds.size}개`}
                                    {keyword.trim() !== "" && ` · 검색 결과 ${filteredCount}개`}
                                </div>
                            </div>

                            <div className="flex gap-4 items-center justify-between">
                                <div className="flex gap-2 items-center">
                                    {/* 600건을 페이지마다 고르는 것은 불가능하다 → 검색 + 전체선택 */}
                                    <Button variants="default"
                                            disabled={filteredCount === 0}
                                            value={keyword.trim() === ""
                                                ? `전체 그룹에 넣기 (${filteredCount}건)`
                                                : `검색 결과 전체 넣기 (${filteredCount}건)`}
                                            onClick={() => toggleMany(filtered, true)}
                                    />
                                    <Button variants="default"
                                            disabled={filteredCount === 0}
                                            value={keyword.trim() === "" ? "전체 빼기" : "검색 결과 전체 빼기"}
                                            onClick={() => toggleMany(filtered, false)}
                                    />
                                </div>
                                <div className="flex gap-4 items-center">
                                    {busy && <div className="text-sm text-gray-500 whitespace-nowrap">{busy}</div>}
                                    {
                                        isDirty && (
                                            <div className="text-sm whitespace-nowrap">
                                                {added.length > 0 && <span className="text-blue-600">+{added.length} </span>}
                                                {removed.length > 0 && <span className="text-red-600">−{removed.length}</span>}
                                            </div>
                                        )
                                    }
                                    <Button variants="dark" value="저장"
                                            disabled={!isDirty || busy != null} onClick={() => save()} />
                                </div>
                            </div>

                            {/* 🔴 max-h + overflow-y-auto — 데이터가 늘어도 팝업이 화면을 넘지 않는다 */}
                            <div className="flex-col border border-gray-200 rounded-lg"
                                 style={{maxHeight: "440px", overflowY: "auto"}}
                            >
                                <div className="flex items-center gap-4 px-4 py-2 border-b border-gray-200 text-sm font-semibold text-gray-600 bg-gray-50 sticky top-0">
                                    <div className="w-6 flex-shrink-0">
                                        <Checkbox checked={pagedAllChecked}
                                                  onChecked={(checked) => toggleMany(paged, checked)} />
                                    </div>
                                    <div className="w-24 flex-shrink-0">타입</div>
                                    <div className="flex-1 min-w-0">진입점명</div>
                                    <div className="w-28 flex-shrink-0">ID</div>
                                    <div className="flex-1 min-w-0">소속 그룹</div>
                                </div>
                                {
                                    paged.map((spot) => {
                                        const inGroup = memberIds.has(spot.id)
                                        return (
                                            <div key={spot.id}
                                                 className={"flex items-center gap-4 px-4 py-3 border-b border-gray-100"
                                                     + (inGroup ? " bg-blue-50" : "")}
                                            >
                                                <div className="w-6 flex-shrink-0">
                                                    <Checkbox checked={inGroup}
                                                              onChecked={(next) => toggleMany([spot], next)} />
                                                </div>
                                                <div className="w-24 flex-shrink-0">
                                                    <Tag color="info">{spotTypeLabel(spot.type)}</Tag>
                                                </div>
                                                <div className="flex-1 min-w-0 truncate">{spot.name}</div>
                                                <div className="w-28 flex-shrink-0"><Tag>{spotShortId(spot.uid)}</Tag></div>
                                                <div className="flex-1 min-w-0 truncate text-xs text-gray-500">
                                                    {(tagsBySpot[spot.id] ?? []).map((c) => `#${c}`).join(" ")}
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                                {
                                    paged.length === 0 && (
                                        <div className="flex-center-all py-16 text-gray-400">
                                            {keyword.trim() === "" ? "등록된 진입점이 없습니다." : "검색 결과가 없습니다."}
                                        </div>
                                    )
                                }
                            </div>

                            <div className="flex justify-center">
                                <Pagination pageSize={PAGE_SIZE} totalCount={filteredCount}
                                            currentPage={page} onPaging={(next) => setPage(next)} />
                            </div>
                        </>
                    )
                }
            </div>
        </SuspensePopup>
    )
}

export default SpotTagManagePopup
